import React, { useState, useEffect } from 'react'
import { TextInputOld, Button, Dropdown, Row, Col, IDropdownItem } from '@doseme/cohesive-ui'
import noop from 'lodash/noop'
import { isLoaded, isLoading } from 'flux-entities'
import moment from 'moment'

import {
  doseMe,
  trial,
  customer,
  TPlanType,
  ISelectCountHashmap,
  IPlanDrug,
  IChangedPlan,
  tdi,
  research
} from '../../../../types/plan'
import { TProps } from './types'
import { PLANS_URL } from '../../../../constants/urls/frontend'
import { FormPageLayout } from '../../../../components/FormPageLayout'
import { StyledForm } from '../../../../components/StyledForm'
import { TFormsValidity } from '../../types'
import { EntitlementsInput } from '../../../../components/EntitlementsInput'
import { IPlanForm } from '../types'

type TKey = 'name' | 'description' | 'type'

const Edit: React.FC<TProps> = ({ plans, fetchPlans, drugs, fetchDrugs, updatePlan, history, match }) => {
  const [copy, updateCopy] = useState<IPlanForm>(() => {
    return {
      description: '',
      drugs: {},
      type: 'DoseMe',
      name: ''
    }
  })

  const [meta, updateMeta] = useState(() => {
    return {
      created: moment(),
      id: 0
    }
  })

  const [error, updateError] = useState('')

  const [fieldsValidity, setFieldsValidity] = useState<TFormsValidity<TKey>>({
    name: {
      touched: true,
      valid: true
    },
    description: {
      touched: true,
      valid: true
    },
    type: {
      touched: true,
      valid: true
    }
  })

  useEffect(() => {
    if (!plans.touched) {
      fetchPlans()
    }
  }, [plans.touched, fetchPlans])

  useEffect(() => {
    if (!drugs.touched) {
      fetchDrugs()
    }
  }, [drugs.touched, fetchDrugs])

  useEffect(() => {
    const oldPlan = plans.all[match.params.id]
    if (oldPlan && isLoaded(drugs)) {
      const fill: ISelectCountHashmap = {}
      for (const x of Object.keys(drugs.all)) {
        fill[x] = { selected: false, count: 0 }
      }
      for (const y of oldPlan.drugs) {
        fill[y.id].count = y.maxCourseCount
      }

      updateCopy({
        description: oldPlan.description,
        drugs: fill,
        type: oldPlan.type,
        name: oldPlan.name
      })

      updateMeta({
        created: oldPlan.created,
        id: oldPlan.id
      })
    }
  }, [plans.all, drugs, updateCopy, updateMeta, match.params.id])

  const plan = plans.all[match.params.id]

  if (!plan && isLoaded(plans)) {
    return <div>No plan was found with the id {match.params.id}</div>
  }

  if (!plan || !isLoaded(drugs)) {
    return <div>Loading...</div>
  }

  const setSelectedDrugs = (entitlements: ISelectCountHashmap) => {
    updateCopy({
      ...copy,
      drugs: entitlements
    })
  }

  const handleInput = (name: string, value: string, valid: boolean) => {
    if (valid) {
      updateCopy({ ...copy, [name]: value })
    }

    setFieldsValidity({
      ...fieldsValidity,
      [name]: {
        touched: true,
        valid
      }
    })
  }

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    updateError('')

    const formattedDrugs: IPlanDrug[] = []
    for (const drug of Object.keys(copy.drugs)) {
      if (copy.drugs[drug].count === 0) {
        continue
      }

      formattedDrugs.push({
        id: drugs.all[drug].id,
        maxCourseCount: copy.drugs[drug].count,
        module: drugs.all[drug].module,
        name: drugs.all[drug].name
      })
    }

    const planToSubmit: IChangedPlan = {
      description: copy.description,
      drugs: formattedDrugs,
      type: copy.type,
      name: copy.name,
      id: meta.id,
      created: meta.created,
      updated: moment()
    }

    const { status, message } = await updatePlan(planToSubmit)
    if (status >= 400) {
      updateError(message)
      return
    }

    history.push(PLANS_URL)
  }

  const handlePlanTypeSelect = (item: IDropdownItem) => {
    const type = item.value as TPlanType
    updateCopy({ ...copy, type })
    setFieldsValidity({
      ...fieldsValidity,
      type: {
        touched: true,
        valid: true
      }
    })
  }

  // by this point we have confirmed this exists
  const originalPlan = plans.all[match.params.id]

  const formValid = (Object.keys(fieldsValidity) as TKey[]).every(
    (field) => fieldsValidity[field].valid && fieldsValidity[field].touched
  )

  const options = [{ value: customer }, { value: doseMe }, { value: research }, { value: tdi }, { value: trial }]

  return (
    <FormPageLayout
      title='Edit Plan'
      testid='edit-plan'
      subtitle={`${originalPlan.type} - ${originalPlan.name}`}
      handleBackClick={() => history.push(PLANS_URL)}
    >
      <StyledForm handleSubmit={handleSubmit}>
        {error}
        <Row>
          <Col>
            <TextInputOld
              name='name'
              type='text'
              defaultValue={copy.name}
              data-test='name'
              onBlur={(value, valid) => handleInput('name', value, valid)}
              placeholder='Name'
              label='Name'
              isRequired
            />
          </Col>

          <Col>
            <Dropdown
              fieldState='valid'
              label='Plan Type'
              showSearchThreshold={0}
              data={options}
              placeholder='Select Plan Type'
              onSelect={handlePlanTypeSelect}
              value={copy.type}
            />
          </Col>
        </Row>
        <br />

        <Row>
          <Col>
            <TextInputOld
              name='description'
              type='text'
              defaultValue={copy.description}
              data-test='description'
              onBlur={(value, valid) => handleInput('description', value, valid)}
              placeholder='Description'
              label='Description'
            />
          </Col>
        </Row>
        <br />

        <Row>
          <Col width={8}>
            <EntitlementsInput
              data={Object.values(drugs.all)}
              selectedDrugs={copy.drugs}
              setSelectedDrugs={setSelectedDrugs}
            />
          </Col>
        </Row>
        <br />
        <hr />

        <div className='d-flex w-100 justify-content-end'>
          <Button type='submit' variant='primary' onClick={noop} disabled={!formValid} loading={isLoading(plans)}>
            Update
          </Button>
        </div>
      </StyledForm>
    </FormPageLayout>
  )
}

export { Edit }
